/**
 * English: Org/station manager card with Ops Score + link to station memory detail.
 * Thai: การ์ดผู้จัดการสถานี — Ops Score และลิงก์ความจำสถานี
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import StatusBadge from '../shared/StatusBadge';
import AgentCompetencyDashboard from './AgentCompetencyDashboard';
import { filterSummariesForDevice } from './memoryDeviceRollups';
import { useAgents } from '../../hooks/useAgents';
import { useMemorySummaryDaily } from '../../hooks/useMemorySummary';
import { agentManagementPath } from './agentManagementQuery';
import type { Station } from '../../types';

export const STATION_MANAGER_DETAIL_PATH = '/agent-management/manager';

interface StationManagerSectionProps {
  orgName: string;
  stationId: string;
  stations: Station[];
  onStationChange: (id: string) => void;
  stationsLoading?: boolean;
}

function stationOptionLabel(s: Station): string {
  const org = String((s as Station & { org_name?: string }).org_name ?? '').trim();
  const name = s.name || s.id;
  return org ? `${org} · ${name}` : name;
}

export default function StationManagerSection({
  orgName,
  stationId,
  stations,
  onStationChange,
  stationsLoading = false,
}: StationManagerSectionProps) {
  const { t } = useTranslation();
  const { manager } = useAgents(stationId);
  const stationDaily = useMemorySummaryDaily(orgName, stationId, 30, 0, 45);

  const activeStation = stations.find((s) => s.id === stationId);
  const stationLabel = activeStation?.name || stationId || t('agents.stationManagerNoStation');
  const managerColor = typeof manager?.color === 'string' ? manager.color : '#6366f1';
  const managerName = manager?.name || manager?.id || t('agents.stationManagerNoAgent');
  const managerId = manager?.id ?? '';

  const managerDailyItems = useMemo(
    () => filterSummariesForDevice(stationDaily.data?.items ?? [], '', managerId),
    [stationDaily.data, managerId],
  );

  const managerDetailHref = agentManagementPath(STATION_MANAGER_DETAIL_PATH, orgName, stationId);

  return (
    <section className="rounded-2xl border border-app-border/80 bg-gradient-to-br from-app-surface via-app-muted/15 to-app-surface shadow-sm overflow-hidden">
      <div className="border-b border-app-border/60 bg-app-muted/20 px-4 py-3 sm:px-5">
        <h2 className="text-title-sm font-semibold text-ink">{t('agents.stationManagerTitle')}</h2>
        <p className="mt-0.5 text-sm text-app-text-sec">{t('agents.stationManagerDescription')}</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,280px)_1fr] gap-0 divide-y xl:divide-y-0 xl:divide-x divide-app-border/60">
        <div className="flex flex-col p-4 sm:p-5">
          <div className="flex items-start gap-3 min-w-0">
            <span
              className="mt-1 h-3 w-3 shrink-0 rounded-full ring-2 ring-app-border/80"
              style={{ backgroundColor: managerColor }}
              aria-hidden
            />
            <div className="min-w-0 flex-1 space-y-2">
              <h3 className="text-base font-semibold text-ink truncate">{managerName}</h3>
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/12 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                <BuildingOffice2Icon className="h-3.5 w-3.5 shrink-0" />
                {t('agents.stationManagerOrg', { org: orgName })}
              </span>
              {managerId ? (
                <p className="font-mono text-xs text-app-text-dim truncate" title={managerId}>
                  {managerId}
                </p>
              ) : (
                <p className="text-xs text-app-text-dim">{t('agents.stationManagerNoAgent')}</p>
              )}
              <StatusBadge variant="info" dot className="!text-[11px]">
                {stationLabel}
              </StatusBadge>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            <label
              htmlFor="agent-mgmt-station-select"
              className="text-[10px] font-semibold uppercase tracking-wide text-app-text-dim"
            >
              {t('agents.stationManagerPickStation')}
            </label>
            <select
              id="agent-mgmt-station-select"
              value={stationId}
              onChange={(e) => onStationChange(e.target.value)}
              disabled={stationsLoading}
              className="w-full rounded-lg border border-app-border bg-app-bg px-3 py-2 text-sm text-app-text focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
            >
              {stations.length === 0 ? (
                <option value="">{t('agents.stationManagerNoStation')}</option>
              ) : null}
              {stations.map((s) => (
                <option key={s.id} value={s.id}>
                  {stationOptionLabel(s)}
                </option>
              ))}
            </select>
          </div>

          <Link
            to={managerDetailHref}
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg border border-app-accent/40 bg-app-accent/10 px-4 py-2.5 text-sm font-medium text-app-accent transition-colors hover:bg-app-accent/20"
          >
            {t('agents.stationManagerViewKnowledge')}
            <ArrowRightIcon className="h-4 w-4 shrink-0" />
          </Link>
        </div>

        <div className="p-4 sm:p-5 bg-app-bg/30 min-w-0">
          <AgentCompetencyDashboard
            agentId={managerId || 'station-manager'}
            dailyItems={managerDailyItems}
            loading={stationDaily.loading}
          />
        </div>
      </div>
    </section>
  );
}
